/**
 * HI-SI Dashboard - Futuristic Layout Component
 */

import React, { useState } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import {
  Activity,
  Play,
  Settings,
  Palette,
  Wifi,
  WifiOff,
  RotateCcw,
  Bell,
  X,
  ChevronLeft,
  ChevronRight,
  FlaskConical,
  FolderOpen,
  Download
} from 'lucide-react';
import LogoHi from '@/components/ui/LogoHi';
import { useDashboardServerStore } from '@/stores/dashboard-server';
import { useServerAuth } from '@/hooks/useAuth-cookie';
import { useActiveDeploymentsCount } from '@/hooks/useDeployments';
import { formatTimeAgo } from '@/utils/format';
import { cn } from '@/utils/cn';

const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { isAdmin } = useServerAuth();
  const activeDeploymentsCount = useActiveDeploymentsCount();

  const {
    connectionStatus,
    activeExecutionsCount,
    totalCostIncurred,
    notifications,
    removeNotification,
    clearNotifications
  } = useDashboardServerStore();
  
  // Support both brand routings: root ("/") and nested under "/dashboard"
  const base = location.pathname.startsWith('/dashboard') ? '/dashboard' : '';

  // Base navigation items
  const baseNavigation = [
    {
      name: 'Dashboard',
      href: base || '/',
      icon: Activity,
      current: location.pathname === (base || '/')
    },
    {
      name: 'Profiles',
      href: `${base}/settings/profiles`,
      icon: Palette,
      current: location.pathname.startsWith(`${base}/settings/`) && location.pathname.includes('/profiles')
    },
    {
      name: 'Projects',
      href: `${base}/projects`,
      icon: FolderOpen,
      current: location.pathname.startsWith(`${base}/projects`)
    },
    {
      name: 'Executions',
      href: `${base}/executions`,
      icon: Play,
      current: location.pathname.startsWith(`${base}/executions`) || location.pathname.startsWith(`${base}/execution/`),
      badge: activeExecutionsCount > 0 ? activeExecutionsCount.toString() : undefined
    },
    {
      name: 'Deployments',
      href: `${base}/deployments`,
      icon: Download,
      current: location.pathname.startsWith(`${base}/deployments`),
      badge: activeDeploymentsCount > 0 ? activeDeploymentsCount.toString() : undefined
    },
    {
      name: 'Settings',
      href: `${base}/settings`,
      icon: Settings,
      current: location.pathname.startsWith(`${base}/settings`) && !location.pathname.includes('/profiles')
    }
  ];

  // Add Demo item for admin users only
  const navigation = isAdmin
    ? [
        ...baseNavigation,
        {
          name: 'Demo',
          href: `${base}/demo`,
          icon: FlaskConical,
          current: location.pathname.startsWith(`${base}/demo`)
        }
      ]
    : baseNavigation;

  const ConnectionIndicator = () => {
    const getIcon = () => {
      if (connectionStatus === 'connected') return <Wifi className="w-4 h-4" />;
      if (connectionStatus === 'reconnecting') return <RotateCcw className="w-4 h-4 animate-spin" />;
      return <WifiOff className="w-4 h-4" />;
    };

    const getColor = () => {
      if (connectionStatus === 'connected') return 'text-cyan-400';
      if (connectionStatus === 'reconnecting') return 'text-yellow-400';
      return 'text-red-400';
    };

    return (
      <div
        className={cn(
          'flex items-center w-full text-xs font-mono',
          getColor(),
          isCollapsed ? 'justify-center' : 'justify-start space-x-2'
        )}
      >
        {getIcon()}
        {!isCollapsed && (
          <span className="uppercase tracking-wider">{connectionStatus}</span>
        )}
      </div>
    );
  };

  const NotificationPanel = () => {
    if (notifications.length === 0) return null;

    return (
      <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
        {notifications.slice(0, 5).map((notification) => (
          <div
            key={notification.id}
            className={cn(
              'p-4 rounded-lg border shadow-lg backdrop-blur-md fade-in',
              'bg-black/80 border-cyan-500/20',
              notification.type === 'error' && 'border-red-500/30',
              notification.type === 'warning' && 'border-yellow-500/30',
              notification.type === 'success' && 'border-green-500/30',
              notification.type === 'info' && 'border-cyan-500/30'
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h4 className={cn(
                  'text-sm font-medium',
                  notification.type === 'error' && 'text-red-400',
                  notification.type === 'warning' && 'text-yellow-400',
                  notification.type === 'success' && 'text-green-400',
                  notification.type === 'info' && 'text-cyan-400'
                )}>
                  {notification.title}
                </h4>
                {notification.message && (
                  <p className="mt-1 text-sm text-white/60">
                    {notification.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-white/40 font-mono">
                  {formatTimeAgo(notification.timestamp)}
                </p>
              </div>
              <button
                onClick={() => removeNotification(notification.id)}
                className="ml-2 text-white/40 hover:text-white/60 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        
        {notifications.length > 5 && (
          <div className="text-center">
            <button
              onClick={clearNotifications}
              className="text-sm text-cyan-400/60 hover:text-cyan-400 transition-colors font-mono"
            >
              CLEAR ALL
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Futuristic Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 bg-black/90 backdrop-blur-xl border-r border-cyan-500/10 transition-all duration-300",
        isCollapsed ? "w-20" : "w-64"
      )}>
        {/* Logo */}
        <div className="flex items-center px-6 py-6 border-b border-cyan-500/10">
          <div className={cn("flex items-center", isCollapsed ? "justify-center w-full" : "space-x-3")}>
            <div className="relative" aria-label="HI‑SI">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/15 to-purple-500/20 rounded-lg flex items-center justify-center relative shadow-[0_0_12px_rgba(34,211,238,0.15)]">
                <LogoHi size={34} />
              </div>
              <div className="absolute inset-0 bg-cyan-500/20 rounded-lg animate-ping" style={{ animationDuration: '3s' }} />
            </div>
            {!isCollapsed && (
              <div>
                <h1 className="text-lg font-light tracking-wider text-cyan-400">HI-SI</h1>
                <p className="text-xs text-white/40 font-mono uppercase">Control System</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="px-3 py-6 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex items-center px-4 py-3 text-sm font-mono rounded-lg transition-all duration-300 group relative',
                  item.current
                    ? 'bg-gradient-to-r from-cyan-500/20 to-transparent border border-cyan-500/30 text-cyan-400'
                    : 'text-white/50 hover:text-white/80 hover:bg-white/5 border border-transparent',
                  isCollapsed && "justify-center px-3"
                )}
                title={isCollapsed ? item.name : undefined}
              >
                <Icon className={cn(
                  "w-5 h-5 transition-all",
                  item.current ? 'text-cyan-400' : 'text-white/40 group-hover:text-white/60',
                  !isCollapsed && "mr-3"
                )} />
                {!isCollapsed && (
                  <>
                    <span className="flex-1 uppercase tracking-wider text-xs">{item.name}</span>
                    {item.badge && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
                {item.current && !isCollapsed && (
                  <div className="w-1 h-4 bg-cyan-400 rounded-full ml-2 animate-pulse" />
                )}
                {/* Badge for collapsed state */}
                {isCollapsed && item.badge && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500/30 text-cyan-400 text-xs rounded-full flex items-center justify-center border border-cyan-500/50">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        
        {/* Status Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-cyan-500/10 bg-black/50">
          <div className="space-y-3">
            <ConnectionIndicator />
            
            {!isCollapsed && (
              <div className="text-xs text-white/40 font-mono space-y-1">
                <div className="flex justify-between">
                  <span className="uppercase">Active:</span>
                  <span className="text-cyan-400">{activeExecutionsCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="uppercase">Cost:</span>
                  <span className="text-green-400">${totalCostIncurred.toFixed(4)}</span>
                </div>
              </div>
            )}

            {/* Notification Bell */}
            <div className={cn("flex items-center", isCollapsed ? "justify-center" : "justify-center pt-2")}>
              <div className="relative">
                <Bell className={cn(
                  "w-5 h-5 transition-colors",
                  notifications.length > 0 ? 'text-cyan-400' : 'text-white/20'
                )} />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
                )}
              </div>
            </div>

            {/* Collapse Toggle */}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="w-full py-2 flex items-center justify-center bg-black/50 border border-cyan-500/20 rounded-lg
                       text-cyan-400/60 hover:text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/30
                       transition-all duration-300 group"
            >
              {isCollapsed ? (
                <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              ) : (
                <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Main Content - Adjusts based on sidebar state */}
      <div className={cn(
        "transition-all duration-300",
        isCollapsed ? "pl-20" : "pl-64"
      )}>
        <main>
          <Outlet />
        </main>
      </div>
      
      {/* Notification Panel */}
      <NotificationPanel />
    </div>
  );
};

export default DashboardLayout;